import { container } from 'src/StaticContainer';
import { z } from 'zod';

const log = container.get('Logger').getSubLogger({ name: 'column-visibility-storage' });

const columnVisibilitySchema = z.record(z.string(), z.boolean());

export type StoredColumnVisibility = Record<string, boolean>;

/**
 * Read a persisted column-visibility map from localStorage. Missing data reads
 * as `{}`; corrupt data (bad JSON or non-boolean values) is removed so the
 * next load starts clean instead of warning on every visit.
 */
export function readStoredColumnVisibility(storageKey: string): StoredColumnVisibility {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return {};
        const parsed = columnVisibilitySchema.safeParse(JSON.parse(raw));
        if (parsed.success) return parsed.data;
        log.warn({
            message: 'Discarding malformed persisted column visibility',
            storageKey,
            issues: parsed.error.issues,
        });
        localStorage.removeItem(storageKey);
        return {};
    } catch (error) {
        log.warn({ message: 'Failed to read persisted column visibility', error: error as Error, storageKey });
        try {
            localStorage.removeItem(storageKey);
        } catch {
            // Storage itself is unavailable; nothing to clean up.
        }
        return {};
    }
}

/** Persist a column-visibility map to localStorage. Best-effort: storage failures are swallowed. */
export function writeStoredColumnVisibility(storageKey: string, visibility: Readonly<StoredColumnVisibility>): void {
    try {
        localStorage.setItem(storageKey, JSON.stringify(visibility));
    } catch (error) {
        log.warn({ message: 'Failed to persist column visibility', error: error as Error, storageKey });
    }
}
